import { styled } from '@mui/material/styles';
import { Button, Card, CardContent, Chip, Grid, Tooltip } from '@mui/material';
import WdPageSimple from '@main/core/WdPageSimple';
import {
  BasicSelect,
  ButtonWithExpand,
  ComposedHeader,
  ComposedTable,
  InputSearch,
  Paginator,
} from '@main/components';
import { DateRangePicker } from '@mui/lab';
import { useContext, memo, useState } from 'react';
import history from '@history';
import { useRequest } from '@main/hooks';
import { searchSubscriptions, statusSubscription } from 'src/app/api/stripe.subscriptions';
import MobileOffIcon from '@mui/icons-material/MobileOff';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { formatDate } from 'src/app/utils/dates';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/core/messageSlice';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';

const Root = styled(WdPageSimple)(({ theme }) => ({
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const statusLicensesData = [
  {
    label: 'Todo',
    value: '',
  },
  {
    label: 'Activo',
    value: 'true',
  },
  {
    label: 'Inactivo',
    value: 'false',
  },
];

const tableRowScheme = [
  {
    title: 'Id stripe',
    width: '180px',
    minWidth: '180px',
  },
  {
    title: 'Id',
    minWidth: '100px',
    width: '320px',
  },
  {
    title: 'Nombre de suscriptor',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Correo de suscriptor',
    minWidth: '80px',
  },
  {
    title: 'Recurrencia de suscripción',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Estatus',
    width: '200px',
    minWidth: '200px',
  },
  {
    title: 'Nombre proyecto',
    width: '180px',
    minWidth: '180px',
  },
  {
    title: 'Clave proyecto',
    width: '240px',
    minWidth: '240px',
  },
  {
    title: 'Fecha último pago',
    minWidth: '120px',
  },
  {
    title: 'Operaciones',
    minWidth: '120px',
  },
  {
    title: 'Fecha creación',
    minWidth: '120px',
  },
  {
    title: 'Opciones',
    minWidth: '240px',
    fixedRight: true,
  },
];

const OptionButtons = memo(({ status = false, onToggleStatus, onUpdate, onReset }) => {
  return (
    <div className="flex w-full flex-wrap flex-row justify-between items-center">
      <Tooltip title="Restaurar registro de terminal">
        <span>
          <Button
            className="my-3"
            onClick={onReset}
            size="small"
            variant="contained"
            color="primary"
            sx={{ minWidth: '3.2rem', aspectRatio: 1 / 1, padding: '0rem' }}
          >
            <RestartAltIcon />
          </Button>
        </span>
      </Tooltip>

      <Tooltip title="Actualizar informacion de registro de terminal">
        <span>
          <Button
            className="my-3"
            onClick={onUpdate}
            size="small"
            variant="contained"
            color="secondary"
            sx={{ minWidth: '3.2rem', aspectRatio: 1 / 1, padding: '0rem' }}
          >
            <EditOutlinedIcon />
          </Button>
        </span>
      </Tooltip>

      <Button
        className="my-3"
        onClick={onToggleStatus}
        size="small"
        variant="contained"
        color="primary"
        style={{ width: '120px', justifyContent: 'center' }}
        startIcon={status ? <MobileOffIcon /> : <MobileFriendlyIcon />}
      >
        {status ? 'Deshabilitar' : 'Habilitar'}
      </Button>
    </div>
  );
});

const Plans = () => {
  const Dispatch = useDispatch();

  const { finalizeSession } = useContext(BootstrapContext);
  // ------ States correspondiente a componentes de filtrado -------
  const [dataList, setDataList] = useState([]);
  const [querySearch, setQuerySearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(25);
  const [datePickerValue, setDatePickerValue] = useState([null, null]);
  const [form, setForm] = useState({ query: '', initDate: '', endDate: '', paginate: undefined });

  // ------- Selects --------------
  const [statusSelect, setStatusSelect] = useState('');

  // ----- Instancia de request que se usaran en la vista junto a sus valores de estado ---------

  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };
  const {
    makeRequest: tryGetData,
    isLoading,
    response,
  } = useRequest(searchSubscriptions, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });
  const { makeRequest: tryEnableTerminal } = useRequest(statusSubscription);

  // ------ Funciones para actualizar los filtros ------
  const handleSearchQuery = (query = '') => {
    if (!query.length > 3) {
      return;
    }
    setQuerySearch(query);
  };

  // ---- Borra todos los filtros ------
  const handleClearAllFilters = () => {
    setQuerySearch('');
    setCurrentPage(1); // Establecer a la primera página
    setCurrentItemsPerPage(25);
    setDatePickerValue([null, null]);
    // _ _ _ Limpeza de ComboBox
    setStatusSelect('');
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangeItems = (items) => {
    setCurrentItemsPerPage(items);
    setCurrentPage(1);
  };

  // ----- Acciones de botones en la tabla ----------
  const handleCreate = () => {
    history.push('/kiosks/create');
  };

  const handleUpdate = (id) => {
    history.push(`/kiosks/update?id=${id}`);
  };
  return (
    <Root
      header={
        <ComposedHeader title="Planes " description="Análisis de datos">
          <div className="px-4 py-4">
            <ButtonWithExpand
              // title={!isLoadingExport ? 'Descargar' : `Descargando ${progressExport}%`}
              title="Descargar"
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="success"
              // startIcon={
              //   isLoadingExport ? (
              //     <CircularProgress
              //       color="inherit"
              //       size="1.8rem"
              //       variant={progressExport > 1 ? 'determinate' : 'indeterminate'}
              //       value={progressExport}
              //     />
              //   ) : (
              //     <SimCardDownloadOutlinedIcon />
              //   )
              // }
            >
              {/* {formats.map((option, index) => (
                        <ButtonWithExpand.Item
                          disabled={isLoadingExport}
                          key={`${index}_${option.value}_doc`}
                          text={`Documento ${option.label}`}
                          onClick={() => handleExportFile(option.value)}
                        />
                      ))} */}
            </ButtonWithExpand>
          </div>
        </ComposedHeader>
      }
      content={
        <Grid container className="px-32 pb-32 pt-0 flex flex-row w-full justify-center">
          <Grid item xs={12} md={12} className="flex w-full">
            <Card className="pb-24 px-24 mt-16 w-full">
              <CardContent className="flex w-full h-full flex-col justify-between">
                <div className="flex flex-col mt-24">
                  <Grid container className="w-full flex justify-center items-center py-8 mb-28">
                    <Grid item xs={12} md={7}>
                      <div className="flex flex-row flex-wrap justify-start items-center">
                        <div className="ml-4 mr-8 my-2 w-8/12 md:my-0">
                          <InputSearch
                            value={querySearch}
                            onChange={(query) => handleSearchQuery(query)}
                          />
                        </div>
                        <div className="ml-4 my-2 md:my-0">
                          <Button
                            fullWidth
                            size="medium"
                            variant="contained"
                            color="primary"
                            onClick={handleClearAllFilters}
                          >
                            Limpiar filtros
                          </Button>
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <div className="flex flex-row justify-center md:justify-end items-center md:mt-0 mt-8">
                        <DateRangePicker
                          slotProps={{ textField: { size: 'small' } }}
                          localeText={{ start: 'Fecha inicial', end: 'Fecha final' }}
                          value={datePickerValue}
                          onChange={(current) => setDatePickerValue(current)}
                          disableFuture
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} className="p-2">
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row flex-wrap justify-start items-center">
                        <div className="px-4">
                          <BasicSelect
                            data={statusLicensesData}
                            onChange={(data) => setStatusSelect(data)}
                            title="Estatus"
                            value={statusSelect}
                            size="small"
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={6} className="p-8 mb-24">
                      <div className="flex flex-row justify-end">
                        <Paginator
                          totalPages={totalPages}
                          totalItems={totalItems}
                          onChangeCurrentPage={(page) => handleChangePage(page)}
                          onChangeItemsPerPage={(items) => handleChangeItems(items)}
                          currentPage={currentPage}
                          currentItemsPerPage={currentItemsPerPage}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <div className="flex flex-col justify-center">
                    <ComposedTable
                      isLoading={isLoading}
                      className="w-full"
                      headers={tableRowScheme}
                      data={dataList}
                    >
                      <ComposedTable.Column content={({ name }) => name} />
                      <ComposedTable.Column content={({ key }) => key} />
                      <ComposedTable.Column content={({ name }) => name} />
                      <ComposedTable.Column
                        content={({ status }) => (
                          <Chip
                            label={status ? 'Activo' : 'Inactivo'}
                            color={status ? 'success' : 'error'}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      />
                      <ComposedTable.Column content={({ deviceId }) => deviceId || 'No asociado'} />
                      <ComposedTable.Column content={({ model }) => model || 'No asociado'} />
                      <ComposedTable.Column content={({ locationName }) => locationName} />
                      <ComposedTable.Column content={({ companyName }) => companyName} />
                      <ComposedTable.Column content={(item) => formatDate(item.created_at)} />
                      <ComposedTable.Column
                        fixedRight
                        content={({ id, status }) => (
                          <OptionButtons status={status} onUpdate={() => handleUpdate(id)} />
                        )}
                      />
                    </ComposedTable>
                  </div>
                </div>
                <Grid container spacing={1} className="p-2 mt-24 flex">
                  <Grid item xs={12} md={12} className="py-8">
                    <div className="flex flex-row justify-end">
                      <Paginator
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onChangeCurrentPage={(page) => handleChangePage(page)}
                        onChangeItemsPerPage={(items) => handleChangeItems(items)}
                        currentPage={currentPage}
                        currentItemsPerPage={currentItemsPerPage}
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      }
    />
  );
};

export default Plans;

